/** Pure storage helpers for a person's non-travelling form-order preference. */
import type { FormOrder } from './views.js'

export type FormOrderMap = Record<string, FormOrder>

/**
 * The persona-scoped half of the bag (#2009).
 *
 * ── why a reserved key rather than a second setting ───────────────────────
 *
 * Personal order was keyed by ENTITY alone. That deliberately survives a
 * definition-layer change — a person who reordered their own fields should not
 * lose it because an administrator edited the persona default — but it also
 * leaked ACROSS personas: the same person acting as Sales and as Finance got
 * one order for both, on forms that legitimately differ.
 *
 * The persona entries live inside the SAME host-specific setting key, under one
 * reserved name, because the issue's compatibility rule is that the old map
 * keeps working while the new one fills in. Entity keys are registry keys
 * (`core_person`, `crm_opportunity`) and cannot contain `@`, so `@personas`
 * cannot collide with one.
 *
 * The legacy entries are never rewritten or migrated on read. A persona with no
 * explicit order falls back to the entity-only entry, so nobody loses their
 * arrangement the day this ships; the first persona-scoped SAVE is what creates
 * a persona entry, and only for that persona.
 */
export const PERSONA_ORDERS = '@personas' as const

/** One persona's orders, by entity. */
export type PersonaOrders = Record<string, FormOrderMap>

/**
 * The stored bag: entity entries, plus the one reserved persona sub-map.
 *
 * The index signature carries the UNION rather than intersecting two shapes,
 * because an intersection claims every key is a `FormOrder` — including
 * `@personas`, which is not one. The value is narrowed where it is read, by
 * `readOrder`'s shape check, which this bag needs regardless: it arrives as
 * JSON from a settings document and nothing upstream guarantees its shape.
 */
export interface FormOrderBag {
  [key: string]: FormOrder | PersonaOrders | undefined
}

/**
 * Shape-check one stored entry without trusting malformed JSON.
 *
 * Takes `unknown` on purpose: the caller reads out of a bag whose values are a
 * union, and this is where the narrowing belongs. It also means the reserved
 * persona sub-map returns null rather than a half-built order if it is ever
 * read through the legacy path by mistake.
 */
function readOrder(value: unknown): FormOrder | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const mine = value as FormOrder
  const sections = Array.isArray(mine.sections)
    ? mine.sections.filter((section): section is string => typeof section === 'string')
    : undefined
  const fields = mine.fields && typeof mine.fields === 'object' && !Array.isArray(mine.fields)
    ? Object.fromEntries(Object.entries(mine.fields)
      .filter(([, value]) => Array.isArray(value))
      .map(([key, value]) => [key, (value as unknown[])
        .filter((field): field is string => typeof field === 'string')]))
    : undefined
  if (!sections?.length && !Object.keys(fields ?? {}).length) return null
  if (sections?.length) return fields && Object.keys(fields).length ? { sections, fields } : { sections }
  return { fields: fields! }
}

/**
 * Read one entity without trusting malformed JSON from the preference bag.
 *
 * `persona` is optional and the fallback is deterministic: this persona's own
 * entry, then the legacy entity-only entry, then nothing. A caller that passes
 * no persona reads the legacy entry only, which is what a host without a
 * persona concept should see.
 */
export function formOrderFor(
  settings: Record<string, unknown> | null | undefined,
  settingKey: string,
  entity: string,
  persona?: string | null,
): FormOrder | null {
  const all = (settings?.[settingKey] ?? {}) as FormOrderBag
  if (persona) {
    const personas = all[PERSONA_ORDERS] as PersonaOrders | undefined
    const scoped = readOrder(personas?.[persona]?.[entity])
    if (scoped) return scoped
  }
  return readOrder(all[entity])
}

/**
 * Replace one entity while retaining every other entity in the same bag — and,
 * with a persona, every OTHER persona's orders too.
 *
 * The legacy entry is left exactly as it is when writing a persona entry. That
 * is deliberate: it remains the fallback for every persona that has not
 * authored one, so promoting this write into the legacy slot would silently
 * change what a different persona sees.
 */
export function withFormOrder(
  all: FormOrderBag, entity: string, next: FormOrder, persona?: string | null,
): FormOrderBag {
  if (!persona) return { ...all, [entity]: next }
  const personas = (all[PERSONA_ORDERS] as PersonaOrders | undefined) ?? {}
  return {
    ...all,
    [PERSONA_ORDERS]: { ...personas, [persona]: { ...(personas[persona] ?? {}), [entity]: next } },
  }
}

/**
 * Reset one entity without leaving an ambiguous empty preference behind.
 *
 * With a persona this removes ONLY that persona's entry for that entity — not
 * the legacy entry, and not another persona's. After it, that persona falls
 * back to the legacy order again, which is the state it had before anyone
 * authored a persona-scoped one.
 */
export function withoutFormOrder(
  all: FormOrderBag, entity: string, persona?: string | null,
): FormOrderBag {
  if (!persona) {
    const next = { ...all }
    delete next[entity]
    return next
  }
  const personas = all[PERSONA_ORDERS] as PersonaOrders | undefined
  if (!personas?.[persona]) return all
  const mine = { ...personas[persona] }
  delete mine[entity]
  const nextPersonas = { ...personas }
  // An emptied persona leaves no husk: an empty object here would read as "this
  // persona has authored orders" to anything counting them.
  if (Object.keys(mine).length) nextPersonas[persona] = mine
  else delete nextPersonas[persona]
  const next = { ...all }
  if (Object.keys(nextPersonas).length) next[PERSONA_ORDERS] = nextPersonas
  else delete next[PERSONA_ORDERS]
  return next
}

/** Move one item by index. Invalid positions preserve the original list. */
export function moved<T>(items: T[], from: number, to: number): T[] {
  if (from === to) return items
  if (from < 0 || to < 0 || from >= items.length || to >= items.length) return items
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item!)
  return next
}
